/**
 * Plain-English explanations of financing terms, shown inline wherever a term
 * appears. Definitions describe what a term means and why a lender cares; they
 * deliberately avoid stating specific SBA rules or thresholds, which change and
 * vary by lender — those belong in "questions to ask your lender".
 */

export interface GlossaryEntry { term: string; plain: string }

export const GLOSSARY: Record<string, GlossaryEntry> = {
  'working-capital': {
    term: 'Working capital',
    plain: 'Money to run the business day to day — payroll, inventory, rent — while you wait to be paid by customers.',
  },
  'owner-injection': {
    term: 'Owner injection (equity)',
    plain: 'Your own money going into the project. Lenders usually want to see owners share the risk, especially for startups and purchases of a business or property.',
  },
  'debt-service': {
    term: 'Debt service',
    plain: 'The total of your loan payments — principal and interest — over a period, usually a year.',
  },
  dscr: {
    term: 'Debt-service coverage (DSCR)',
    plain: 'How many times over your business income covers your loan payments. 1.0 means income exactly covers payments with nothing left over; lenders want comfortably more than that.',
  },
  collateral: {
    term: 'Collateral',
    plain: 'Property or equipment a lender can claim if the loan is not repaid. Ask what a lender would take as collateral before you sign.',
  },
  'personal-guarantee': {
    term: 'Personal guarantee',
    plain: 'A promise to repay the business loan personally if the business cannot. Ask a lender who would need to sign one.',
  },
  'guaranty-fee': {
    term: 'SBA guaranty fee',
    plain: 'A fee SBA charges on many guaranteed loans, often passed on to the borrower. Ask your lender what it would be on your loan and whether it can be financed.',
  },
  '7a': {
    term: 'SBA 7(a) loan',
    plain: "SBA's main business loan program. A bank or other lender makes the loan; SBA guarantees part of it, which makes lenders more willing to lend.",
  },
  '504': {
    term: 'SBA 504 loan',
    plain: 'Long-term, fixed-rate financing for major fixed assets such as buildings, land and long-life equipment, made through a Certified Development Company with a bank.',
  },
  'charge-off': {
    term: 'Charged off',
    plain: 'When a lender and SBA stop expecting a loan to be repaid and write it off as a loss.',
  },
  prime: {
    term: 'Prime rate',
    plain: 'A benchmark interest rate published by the Federal Reserve. Variable SBA loan rates are usually quoted as prime plus a margin.',
  },
  'variable-rate': {
    term: 'Variable rate',
    plain: 'An interest rate that moves when its benchmark (usually prime) moves, so your payment can change over the life of the loan.',
  },
  'operating-income': {
    term: 'Operating income',
    plain: 'What the business earns from its operations after its running costs, before loan interest and taxes. It is what is available to pay loans.',
  },
  cogs: {
    term: 'Cost of goods sold',
    plain: 'The direct cost of what you sold — materials, inventory, direct labour.',
  },
  'lender-match': {
    term: 'SBA Lender Match',
    plain: "SBA's free tool that connects businesses with lenders who participate in SBA programs.",
  },
};
